

if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        if (this.elements.viewer) {
          this._galleryIntersecting = true;
          this._galleryMuteRestore = new WeakMap();
          this.initViewportMuteObserver();
        }

        if (!this.elements.thumbnails) {
          this._schedulePreloadGalleryVideos();
          return;
        }

        // Play media immediately on slide change so mobile browsers still treat it as
        // user-driven; debouncing the whole handler delays play() past the gesture window.
        this._debouncedSyncThumbnails = debounce(
          this.syncThumbnailsForSlide.bind(this),
          200
        );
        this.elements.viewer.addEventListener("slideChanged", (event) => {
          this.onSlideChangedImmediate(event);
          this._debouncedSyncThumbnails(event);
        });

        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector("button");
            if (button) {
              button.addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            }
          });

        if (
          this.dataset.desktopLayout?.includes("thumbnail") &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        this._schedulePreloadGalleryVideos();
      }

      disconnectedCallback() {
        clearTimeout(this._viewportMuteHideTimer);
        this._viewportMuteObserver?.disconnect();
      }

      initViewportMuteObserver() {
        if (!window.IntersectionObserver || !this.elements.viewer) return;
        const HIDE_DELAY_MS = 500;
        this._viewportMuteObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            if (entry.isIntersecting) {
              clearTimeout(this._viewportMuteHideTimer);
              this._viewportMuteHideTimer = null;
              this._galleryIntersecting = true;
              this._applyGalleryViewportMute(true);
            } else {
              clearTimeout(this._viewportMuteHideTimer);
              this._viewportMuteHideTimer = setTimeout(() => {
                this._viewportMuteHideTimer = null;
                this._galleryIntersecting = false;
                this._applyGalleryViewportMute(false);
              }, HIDE_DELAY_MS);
            }
          },
          { root: null, threshold: 0, rootMargin: "0px" }
        );
        this._viewportMuteObserver.observe(this.elements.viewer);
      }

      _forEachGalleryVideo(callback) {
        this.querySelectorAll("deferred-media video").forEach(callback);
      }

      _applyGalleryViewportMute(isVisible) {
        if (isVisible) {
          this._forEachGalleryVideo((video) => {
            if (this._galleryMuteRestore.has(video)) {
              video.muted = this._galleryMuteRestore.get(video);
              this._galleryMuteRestore.delete(video);
            }
          });
        } else {
          this._forEachGalleryVideo((video) => {
            if (!this._galleryMuteRestore.has(video)) {
              this._galleryMuteRestore.set(video, video.muted);
            }
            video.muted = true;
          });
        }
      }

      applyHiddenMuteIfNeeded() {
        if (
          !this._galleryMuteRestore ||
          this._galleryIntersecting !== false
        )
          return;
        this._forEachGalleryVideo((video) => {
          if (!this._galleryMuteRestore.has(video)) {
            this._galleryMuteRestore.set(video, video.muted);
          }
          video.muted = true;
        });
      }

      _schedulePreloadGalleryVideos() {
        if (!this.elements.viewer) return;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.preloadGalleryVideos();
            queueMicrotask(() => this.applyHiddenMuteIfNeeded());
          });
        });
      }

      preloadGalleryVideos() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const slides = viewer.sliderItemsToShow;
        if (!slides?.length) {
          this._preloadSlideRetry = (this._preloadSlideRetry || 0) + 1;
          if (this._preloadSlideRetry <= 12) {
            requestAnimationFrame(() => this.preloadGalleryVideos());
          }
          return;
        }
        this._preloadSlideRetry = 0;

        const pageIdx = Math.max(0, (viewer.currentPage || 1) - 1);
        const currentEl = slides[pageIdx] ?? slides[0];
        const currentMediaId = currentEl?.dataset?.mediaId;

        const videoDeferreds = Array.from(
          this.querySelectorAll("[data-media-id] deferred-media")
        ).filter((deferred) => {
          if (deferred.getAttribute("loaded")) return false;
          const template = deferred.querySelector("template");
          return Boolean(template?.content?.querySelector("video"));
        });

        const isCurrent = (deferred) =>
          deferred.closest("[data-media-id]")?.dataset?.mediaId ===
          currentMediaId;

        const others = videoDeferreds.filter((d) => !isCurrent(d));
        const current = videoDeferreds.filter((d) => isCurrent(d));

        others.forEach((dm) => dm.loadContent(false, { preloadOnly: true }));
        current.forEach((dm) => dm.loadContent(false));
      }

      onSlideChangedImmediate(event) {
        const activeMedia = this.activeMediaFromSlideEvent(event);
        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        this.playActiveMedia(activeMedia);
      }

      syncThumbnailsForSlide(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement?.dataset?.mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      activeMediaFromSlideEvent(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement?.dataset?.mediaId) return null;

        return currentElement.matches("[data-media-id]")
          ? currentElement
          : currentElement.closest("[data-media-id]") ||
              this.elements.viewer.querySelector(
                `[data-media-id="${currentElement.dataset.mediaId}"]`
              );
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!activeMedia) {
          console.warn(`No media found for ID: ${mediaId}`);
          return;
        }

        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        if (prepend) {
          if (activeMedia.parentElement) {
            activeMedia.parentElement.prepend(activeMedia);
          }
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail && activeThumbnail.parentElement) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement?.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === "stacked"
          ) {
            activeMedia.scrollIntoView({ behavior: "smooth" });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail?.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button")?.setAttribute("aria-current", true);

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        // Force re-triggering load
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia(activeItem);
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
        queueMicrotask(() => this.applyHiddenMuteIfNeeded());
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}



























// if (!customElements.get("media-gallery")) {
//   customElements.define(
//     "media-gallery",
//     class MediaGallery extends HTMLElement {
//       constructor() {
//         super();
//         this.elements = {
//           liveRegion: this.querySelector('[id^="GalleryStatus"]'),
//           viewer: this.querySelector('[id^="GalleryViewer"]'),
//           thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
//         };
//         this.mql = window.matchMedia("(min-width: 750px)");

//         if (!this.elements.thumbnails) return;

//         this.elements.viewer.addEventListener(
//           "slideChanged",
//           debounce(this.onSlideChanged.bind(this), 500)
//         );

//         this.elements.thumbnails
//           .querySelectorAll("[data-target]")
//           .forEach((mediaToSwitch) => {
//             const button = mediaToSwitch.querySelector("button");
//             if (button) {
//               button.addEventListener(
//                 "click",
//                 this.setActiveMedia.bind(
//                   this,
//                   mediaToSwitch.dataset.target,
//                   false
//                 )
//               );
//             }
//           });

//         if (
//           this.dataset.desktopLayout?.includes("thumbnail") &&
//           this.mql.matches
//         ) {
//           this.removeListSemantic();
//         }
//       }

//       onSlideChanged(event) {
//         const thumbnail = this.elements.thumbnails.querySelector(
//           `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
//         );
//         this.setActiveThumbnail(thumbnail);
//       }

//       setActiveMedia(mediaId, prepend) {
//         const activeMedia = this.elements.viewer.querySelector(
//           `[data-media-id="${mediaId}"]`
//         );
//         if (!activeMedia) {
//           console.warn(`No media found for ID: ${mediaId}`);
//           return;
//         }

//         this.elements.viewer
//           .querySelectorAll("[data-media-id]")
//           .forEach((element) => {
//             element.classList.remove("is-active");
//           });
//         activeMedia.classList.add("is-active");

//         if (prepend) {
//           if (activeMedia.parentElement) {
//             activeMedia.parentElement.prepend(activeMedia);
//           }
//           if (this.elements.thumbnails) {
//             const activeThumbnail = this.elements.thumbnails.querySelector(
//               `[data-target="${mediaId}"]`
//             );
//             if (activeThumbnail && activeThumbnail.parentElement) {
//               activeThumbnail.parentElement.prepend(activeThumbnail);
//             }
//           }
//           if (this.elements.viewer.slider) this.elements.viewer.resetPages();
//         }

//         this.preventStickyHeader();
//         window.setTimeout(() => {
//           if (this.elements.thumbnails) {
//             activeMedia.parentElement?.scrollTo({
//               left: activeMedia.offsetLeft,
//             });
//           }
//           if (
//             !this.elements.thumbnails ||
//             this.dataset.desktopLayout === "stacked"
//           ) {
//             activeMedia.scrollIntoView({ behavior: "smooth" });
//           }
//         });

//         this.playActiveMedia(activeMedia);

//         if (!this.elements.thumbnails) return;

//         const activeThumbnail = this.elements.thumbnails.querySelector(
//           `[data-target="${mediaId}"]`
//         );
//         this.setActiveThumbnail(activeThumbnail);
//         this.announceLiveRegion(
//           activeMedia,
//           activeThumbnail?.dataset.mediaPosition
//         );
//       }

//       setActiveThumbnail(thumbnail) {
//         if (!this.elements.thumbnails || !thumbnail) return;

//         this.elements.thumbnails
//           .querySelectorAll("button")
//           .forEach((element) => element.removeAttribute("aria-current"));
//         thumbnail.querySelector("button")?.setAttribute("aria-current", true);

//         if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

//         this.elements.thumbnails.slider?.scrollTo({
//           left: thumbnail.offsetLeft,
//         });
//       }

//       announceLiveRegion(activeItem, position) {
//         const image = activeItem.querySelector(
//           ".product__modal-opener--image img"
//         );
//         if (!image) return;

//         image.onload = () => {
//           this.elements.liveRegion.setAttribute("aria-hidden", false);
//           this.elements.liveRegion.innerHTML =
//             window.accessibilityStrings.imageAvailable.replace(
//               "[index]",
//               position
//             );
//           setTimeout(() => {
//             this.elements.liveRegion.setAttribute("aria-hidden", true);
//           }, 2000);
//         };
//         // Force re-triggering load
//         image.src = image.src;
//       }

//       playActiveMedia(activeItem) {
//         window.pauseAllMedia();
//         const deferredMedia = activeItem.querySelector(".deferred-media");
//         if (deferredMedia) deferredMedia.loadContent(false);
//       }

//       preventStickyHeader() {
//         this.stickyHeader =
//           this.stickyHeader || document.querySelector("sticky-header");
//         if (!this.stickyHeader) return;
//         this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
//       }

//       removeListSemantic() {
//         if (!this.elements.viewer.slider) return;
//         this.elements.viewer.slider.setAttribute("role", "presentation");
//         this.elements.viewer.sliderItems.forEach((slide) =>
//           slide.setAttribute("role", "presentation")
//         );
//       }
//     }
//   );
// }

